import * as z from 'zod';

// --- Backend-derived enums ---

const companyTypeSchema = z.enum([
  'buy-to-retire',
  'buy-to-sell',
  'journalist',
  'consultant',
  'developer',
  'investor',
  'other',
]);

const compassSubscriptionTypeSchema = z.enum(['none', 'full', 'individual']);

// --- Nested schemas ---

const featureSchema = z.object({
  name: z.string(),
  display_name: z.string(),
  category: z.string(),
});

const compassProjectTypeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  project_group_id: z.string().uuid(),
  display_name: z.string(),
  logo: z.string(),
  acronyms: z.array(z.string()),
});

const compassModuleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  display_name: z.string(),
  description: z.string().nullable(),
  project_types: z.array(compassProjectTypeSchema),
  has_permission: z.boolean(),
});

// --- Root /user/me response schema ---

export const userMeResponseSchema = z.object({
  user_id: z.string().uuid(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  features: z.array(featureSchema),
  plan: z.string().nullable(),
  plan_subscription_type: z.string().nullable(),
  is_demo: z.boolean(),
  is_plan_public: z.boolean(),
  compass: z.boolean(),
  compass_subscription_type: compassSubscriptionTypeSchema,
  last_verified_at: z.string().nullable(),
  latest_ui_version: z.string().nullable(),
  company_type: companyTypeSchema.nullable(),
  compass_modules: z.array(compassModuleSchema).default([]),
  resent_count: z.number().optional(),
  show_super_pollutants_terms_modal: z.boolean().optional(),
  // HMAC-SHA256 hash used to verify the user's identity to ClearFeed. Signed on the
  // backend; passed to clearfeed.init({ user.hash }) for identity verification.
  clearfeed_user_hash: z.string().nullable().optional(),
  // Legacy field — kept for backward compatibility
  freemium_experience: z.boolean().optional(),
});

// --- Inferred TypeScript types ---

export type UserMeResponse = z.infer<typeof userMeResponseSchema>;
export type Feature = z.infer<typeof featureSchema>;
export type CompassModule = z.infer<typeof compassModuleSchema>;
export type CompassProjectType = z.infer<typeof compassProjectTypeSchema>;
export type CompanyType = z.infer<typeof companyTypeSchema>;
export type CompassSubscriptionType = z.infer<typeof compassSubscriptionTypeSchema>;
